"use client";

import { type LinkField, type TextField } from "@sitecore-content-sdk/nextjs";
import { ICONS } from "../variables/icons";
import useBreakpoint from "../hooks/useBreakpoint";
import Icon from "./Icon";
import { SitecoreLink as Link } from "./SitecoreLink";
import { firstNonEmpty } from "src/utils";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** The size of the button */
  size?: string;
  /** Additional CSS classes to apply to the button */
  className?: string;
  /** Left or Right position */
  iconPosition?: string;
  /** Set to true to show icon only */
  iconOnly?: boolean;
  jssIconField?: TextField;
  field?: LinkField;
  buttonType?: string;
  disabled?: boolean;
  ariaLabel?: string;
  onClickCustom?: (event?: React.MouseEvent<HTMLElement>) => void;
  isDisableTextLimit?: boolean;
  iconName?: string;
  textColor?: string;
  iconColor?: string;
}

interface VariantMap {
  [key: string]: string;
}

type IconDetails = {
  jssIconField?: TextField | null;
  iconPosition?: string;
};

interface SizeMap {
  [key: string]: string;
}

export const buttonVariants = [
  "primary",
  "primary-reverse",
  "secondary",
  "secondary-reverse",
  "tertiary",
  "tertiary-reverse",
  "numbers",
  "primary-with-icon",
  "secondary-with-icon",
  "tertiary-reverse",
  "tertiary-with-left-side-icon",
  "tertiary-with-right-side-icon",
  "navigation-tertiary-reverse",
];

export const buttonWithIconVariants = [
  "numbers",
  "primary-with-icon",
  "secondary-with-icon",
  "tertiary-with-left-side-icon",
  "tertiary-with-right-side-icon",
  "navigation-tertiary-reverse",
];

export const getIconDetails = (
  className: string,
  iconName = "CHEVRON_BLUE_RIGHT",
): {
  jssIconField?: TextField;
  iconPosition?: string;
} => {
  if (buttonWithIconVariants.includes(className)) {
    return {
      jssIconField: {
        value: ICONS[iconName as keyof typeof ICONS],
      },
      iconPosition:
        className === "tertiary-with-left-side-icon" ? "left" : "right",
    };
  }
  return {};
};

const UNEXPECTED_SCRIPT_SUFFIX = ";return false;";

// Build a sanitized link field instead of mutating the incoming prop:
// - external links open in a new tab,
// - javascript: hrefs ending in `;return false;` get that trailing fragment stripped.
const sanitizeLinkField = (field?: LinkField): LinkField | undefined => {
  if (!field?.value) return field;

  const value = { ...field.value };

  if (value.linktype === "external" && value?.href?.startsWith("http")) {
    value.target = "_blank";
  }

  if (
    value.linktype === "javascript" &&
    typeof value.href === "string" &&
    value.href.endsWith(UNEXPECTED_SCRIPT_SUFFIX)
  ) {
    value.href = value.href.slice(0, -UNEXPECTED_SCRIPT_SUFFIX.length);
  }

  return { ...field, value };
};

const ButtonContent = ({
  icon,
  iconRotateClass,
  iconNavigationClass,
  textCharLimitClass,
  sanitizedField,
  buttonType,
  iconColor,
  isLink,
}: {
  icon: IconDetails;
  iconRotateClass: string;
  iconNavigationClass: string;
  textCharLimitClass: string;
  sanitizedField?: LinkField;
  buttonType?: string;
  iconColor?: string;
  isLink?: boolean;
}) => (
  <>
    {icon.jssIconField?.value && (
      <Icon
        icon={(icon.jssIconField.value as string) ?? ""}
        jssIconTextField={icon.jssIconField}
        className={`${iconRotateClass} ${iconNavigationClass} ${iconColor ?? ""}`}
      />
    )}

    <span
      className={`${textCharLimitClass} ${
        isLink ? `${buttonType}-linktext ` : ""
      }inline-block`}
    >
      {sanitizedField?.value?.text}
    </span>
  </>
);

const Button = ({
  field: scField,
  className,
  size,
  iconPosition,
  iconOnly,
  buttonType,
  disabled,
  jssIconField,
  ariaLabel,
  onClickCustom,
  tabIndex,
  isDisableTextLimit = false,
  iconName = "",
  ...rest
}: ButtonProps) => {
  // Forward only data-* attributes (like data-testid, data-role) to the
  // rendered element. Other passthrough props are intentionally ignored.
  const dataAttrs = Object.fromEntries(
    Object.entries(rest).filter(([key]) => key.startsWith("data-")),
  );

  let icon: IconDetails = { jssIconField, iconPosition };
  if (iconName && !jssIconField && buttonType) {
    icon = getIconDetails(buttonType, iconName);
  }

  const { isDesktop } = useBreakpoint();

  const variant: VariantMap = {
    primary:
      "btn-primary bg-ecolab-blue shadow-btn text-white hover:bg-dark-blue focus:bg-dark-blue  active:[background:var(--color-ecolab-gradient-blue)] disabled:bg-medium-gray disabled:text-text-gray disabled:opacity-40 focus:font-medium",
    "primary-reverse":
      "btn-primary-reverse text-ecolab-blue shadow-btn bg-white hover:text-dark-blue active:text-dark-blue disabled:bg-white/60",
    secondary:
      "btn-secondary text-ecolab-blue shadow-btn bg-white border-ecolab-blue border-[2px] active:border-[3px] [&:focus]:font-medium disabled:border-light-gray outline-offset-3",
    "secondary-reverse":
      "btn-secondary-reverse text-white shadow-btn bg-transparent border-white border-2 [&:focus]:font-medium disabled:border-light-gray hover:border-3 hover:px-[19px] hover:py-[11px] hover:bg-white/10 outline-offset-3",
    tertiary:
      "btn-tertiary text-ecolab-blue disabled:text-light-gray active:text-dark-blue hover:underline active:no-underline rounded-none !p-0",
    "tertiary-reverse":
      "btn-tertiary-reverse text-white disabled:text-light-gray hover:underline rounded-none !p-0 focus-visible:underline",
    numbers: `${rest?.textColor ? rest?.textColor : "text-black"} shadow-btn bg-white hover:text-ecolab-blue focus:text-ecolab-blue active:text-ecolab-blue py-7 px-6 rounded-none border-t-4 border-t-ecolab-blue`,
    "primary-with-icon":
      "btn-primary-with-icon bg-ecolab-blue shadow-btn text-white hover:bg-dark-blue focus:bg-dark-blue active:bg-dark-blue disabled:bg-medium-gray focus:!font-medium active:font-medium disabled:text-text-gray disabled:opacity-40",
    "secondary-with-icon":
      "btn-secondary-with-icon btn-secondary text-ecolab-blue shadow-btn bg-white border-ecolab-blue border-[2px] active:border-[3px] [&:focus]:font-medium disabled:border-light-gray outline-offset-3",
    "tertiary-with-left-side-icon":
      "btn-tertiary-with-left-side-icon justify-self-start text-ecolab-blue disabled:text-light-gray active:text-dark-blue hover:underline active:no-underline rounded-none !p-0",
    "tertiary-with-right-side-icon":
      "btn-tertiary-with-right-side-icon justify-self-start text-ecolab-blue disabled:text-light-gray active:text-dark-blue hover:underline active:no-underline rounded-none !p-0",
    "navigation-tertiary-reverse":
      "btn-navigation-tertiary-reverse flex flex-nowrap justify-center items-center content-center text-white font-normal focus:font-medium xl:hover:bg-[var(--hover-effect-opacity)] xl:leading-[17.6px] xl:px-4 xl:pb-[11px] xl:pt-[15px] hover:underline active:underline focus:underline xl:active:no-underline xl:focus:no-underline xl:hover:no-underline rounded-none w-fit",
  };

  const sizes: SizeMap = {
    lg: "text-lg leading-[1.3] px-4 py-2.5",
    sm: "text-xs leading-[1.5] px-[10px] py-[5px]",
    xs: "text-xs leading-[1.5] px-[5px] py-px",
    default: "text-base py-[12px] px-[20px] mr-[10px]",
  };

  const sizeClass = size && sizes[size] ? sizes[size] : sizes.default;
  const iconDirectionClass =
    icon?.iconPosition === "right" ? "flex-row-reverse" : "";
  const iconOnlyClass = iconOnly ? "icon-only" : "";
  const variantClass = (buttonType && variant[buttonType]) || "";
  const jssIconFieldClass = icon.jssIconField ? "inline-flex gap-2" : "";
  const textCharLimitClass = isDisableTextLimit
    ? ""
    : "line-clamp-1 max-w-[32ch] truncate";

  const isNavigationTertiaryReverse =
    buttonType === "navigation-tertiary-reverse";
  const classNames = isNavigationTertiaryReverse
    ? `${variantClass} ${iconDirectionClass}`
    : `button-component flex flex-nowrap! justify-center items-center content-center rounded-full font-bold min-h-[52px] min-w-[123px] leading-[22.8571px] tracking-[0.4px] mb-[15px] md:mb-0 max-sm:max-w-full ${sizeClass} ${iconDirectionClass} ${iconOnlyClass} ${className} ${variantClass} ${jssIconFieldClass}`;

  const navigationIconVisibilityClass = isDesktop ? "hidden" : "w-4 h-4 ml-2";
  const iconNavigationClass = isNavigationTertiaryReverse
    ? navigationIconVisibilityClass
    : "";

  const sanitizedField = sanitizeLinkField(scField);

  const accessibleLabel =
    firstNonEmpty(
      ariaLabel,
      sanitizedField?.value?.title,
      sanitizedField?.value?.text,
    ) ?? "";

  const accessibleTitle =
    firstNonEmpty(ariaLabel, sanitizedField?.value?.text) ?? "";

  const noStyleSelect = {
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
  } as const;

  const iconRotateClass =
    buttonType === "tertiary-with-left-side-icon"
      ? "transform-[rotate(180deg)]"
      : "";

  if (onClickCustom) {
    return (
      <button
        className={classNames}
        disabled={disabled}
        onClick={(event) => {
          event.preventDefault();
          onClickCustom(event);
        }}
        tabIndex={tabIndex}
        aria-label={accessibleLabel}
        title={accessibleTitle}
        style={noStyleSelect}
        {...dataAttrs}
      >
        <ButtonContent
          icon={icon}
          iconRotateClass={iconRotateClass}
          iconNavigationClass={iconNavigationClass}
          textCharLimitClass={textCharLimitClass}
          sanitizedField={sanitizedField}
          iconColor={rest.iconColor}
        />
      </button>
    );
  }

  return (
    <Link
      className={classNames}
      field={sanitizedField as LinkField}
      aria-disabled={disabled}
      tabIndex={tabIndex}
      aria-label={accessibleLabel}
      title={accessibleTitle}
      style={noStyleSelect}
      {...dataAttrs}
    >
      <ButtonContent
        icon={icon}
        iconRotateClass={iconRotateClass}
        iconNavigationClass={iconNavigationClass}
        textCharLimitClass={textCharLimitClass}
        sanitizedField={sanitizedField}
        buttonType={buttonType}
        iconColor={rest.iconColor}
        isLink
      />
    </Link>
  );
};

export default Button;
